import { useCallback, useEffect, useState } from 'react';
import APIErrorsHandlingUtils from 'utils/APIErrorsHandlingUtils';
import { useLanguage } from 'hooks/useLanguage';

/**
 * Завантажує один запис за ідентифікатором.
 *
 * Сторінки заходу й новини раніше не робили власного запиту: вони брали всю
 * колекцію зі сховища і шукали в ній потрібний запис. Через це посилання на
 * захід, що вже минув, вело в нікуди — календар свідомо віддає лише майбутні
 * заходи, тому запис у списку не знаходився, стан лишався порожнім, і сторінка
 * назавжди зависала на лоадері. Тепер такі сторінки питають запис прямо, а
 * бекенд на неіснуючий ідентифікатор відповідає 404.
 *
 * Зміна мови перезапитує запис: переклад живе окремим записом CMS, тому
 * дістати його можна лише новим запитом.
 *
 * Повертає { data, status, error, retry }.
 */
export function useRecord(fetcher, id) {
  const [state, setState] = useState({ data: null, status: 'loading', error: null });
  const [attempt, setAttempt] = useState(0);
  const language = useLanguage();

  useEffect(() => {
    let cancelled = false;

    if (id === undefined || id === null || id === '') {
      setState({ data: null, status: 'failed', error: { kind: 'notFound', status: 404 } });
      return undefined;
    }

    setState((previous) => ({ ...previous, status: 'loading', error: null }));

    fetcher(id, language).then(([error, data]) => {
      if (cancelled) return;

      if (error) {
        setState({ data: null, status: 'failed', error: APIErrorsHandlingUtils.classify(error) });
      } else {
        setState({ data, status: 'succeeded', error: null });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetcher, id, language, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  return { ...state, retry };
}
